import { apiGet } from './apiConfig';
import type {
  Risk,
  CashflowStatement,
  IncomeStatement,
  BalanceSheet,
  ClientOverview,
  PortfolioOverviewResponse,
  RiskFeedPage,
  RiskQueryParams,
  FinancialDataQueryParams
} from '../../types/api';

/**
 * Data API Service
 * Handles all financial data endpoints
 */
export class DataService {
  private overviewCache = new Map<string, Promise<PortfolioOverviewResponse>>();

  /**
   * GET /risks/ — rows match backend `Risks` (source_type, originator, is_positive, etc.).
   */
  async getRisks(params?: RiskQueryParams): Promise<Risk[]> {
    return await apiGet('/risks/', params);
  }

  /**
   * GET /risks/?limit=... — cursor-paginated risk feed used after the
   * Overview's initial bounded page has rendered.
   */
  async getRiskPage(params: RiskQueryParams): Promise<RiskFeedPage> {
    return await apiGet('/risks/', params);
  }

  /**
   * Get cashflow statements for a specific user and unit
   */
  async getCashflow(params: FinancialDataQueryParams): Promise<CashflowStatement[]> {
    return await apiGet('/cashflow/', params);
  }

  /**
   * Get income statements for a specific user and unit
   */
  async getIncomeStatement(params: FinancialDataQueryParams): Promise<IncomeStatement[]> {
    return await apiGet('/income_statement/', params);
  }

  /**
   * Get balance sheets for a specific user and unit
   */
  async getBalanceSheet(params: FinancialDataQueryParams): Promise<BalanceSheet[]> {
    return await apiGet('/balance_sheet/', params);
  }

  /**
   * Get client overview data for a specific user and unit
   */
  async getClientOverview(params: FinancialDataQueryParams): Promise<ClientOverview[]> {
    return await apiGet('/client_overview/', params);
  }

  /**
   * One bounded TTM response shared by all Overview widgets.
   * Cache in-flight and completed calls so one page mount never fans out into
   * duplicate financial requests.
   */
  getPortfolioOverview(
    userId: number,
    unitId?: number,
    currency = 'USD',
    riskLimit = 20,
  ): Promise<PortfolioOverviewResponse> {
    const cacheKey = `${userId}:${unitId ?? 'portfolio'}:ttm:${riskLimit}`;
    const cached = this.overviewCache.get(cacheKey);
    if (cached) return cached;

    const request = apiGet('/portfolio-overview', {
      user_id_no: userId,
      unit_id_no: unitId,
      period: 'ttm',
      currency,
      risk_limit: riskLimit,
    }).catch((error) => {
      this.overviewCache.delete(cacheKey);
      throw error;
    });
    this.overviewCache.set(cacheKey, request);
    return request;
  }

  clearPortfolioOverviewCache(): void {
    this.overviewCache.clear();
  }
}

// Create and export a single instance
export const dataService = new DataService();

